import Button from "@mui/material/Button";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useAppState } from "../state/AppState";
import { useLang } from "../i18n/LangContext";
import { DEFAULT_SOURCES, todayIso } from "../state/filters";

/** Discreet bottom-centre pill that surfaces when ANY filter deviates
 *  from the default state. Tap to reset all filters at once and see
 *  every spot again. Hidden on the map view when no filter is active,
 *  to keep the canvas uncluttered. */
export function FilterResetPill() {
  const { spots, visibleSpots, filters, dispatchFilter } = useAppState();
  const { t } = useLang();

  const isFilterActive =
    filters.q !== "" ||
    filters.species !== "" ||
    filters.method !== "" ||
    filters.onlyInSeason ||
    filters.enabledSources.size < DEFAULT_SOURCES.length ||
    filters.date !== todayIso();

  if (!isFilterActive) return null;

  return (
    <Button
      onClick={() => dispatchFilter({ type: "reset" })}
      startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
      aria-label={`${visibleSpots.length} / ${spots.length} · ${t("reset")}`}
      sx={{
        position: "fixed",
        left: "50%",
        bottom: "calc(20px + var(--sab))",
        transform: "translateX(-50%)",
        zIndex: 6,
        // Translucent dark surface so the map stays visible behind it;
        // backdrop-blur keeps the label readable over busy basemaps.
        // Single-line whiteSpace prevents German labels (Zurücksetzen)
        // from wrapping onto a second row.
        bgcolor: "rgba(11, 19, 32, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        color: "text.primary",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: 999,
        px: 1.75,
        py: 0.75,
        fontSize: 13,
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: 0,
        whiteSpace: "nowrap",
        boxShadow: "0 4px 16px -6px rgba(0, 0, 0, 0.35)",
        "&:hover": {
          bgcolor: "rgba(17, 28, 46, 0.75)",
          borderColor: "rgba(56, 189, 248, 0.45)",
        },
        "& .MuiButton-startIcon": { mr: 0.75, ml: -0.25 },
      }}
    >
      {visibleSpots.length} / {spots.length}
    </Button>
  );
}
